import React, { useEffect, useRef, useState } from "react";
import { resolveMediaV2, resolvePlayableMp4V2, type ResolveInput } from '@/resolvers/media';
import { useActiveVideoManager } from '@/hooks/useActiveVideoManager';
import { useInViewport } from "@/hooks/useInViewport";
import { mlog, mwarn } from "@/utils/mediaDebug";

type Props = {
  mode: 'lite' | 'full';
  provider: 'cloudinary' | 'none';
  cloudPublicId?: string;
  posterPublicId?: string;
  className?: string;
  autoOnVisible?: boolean;
  externalVideoRef?: React.RefObject<HTMLVideoElement>;
  onPlay?: () => void;
  onError?: () => void;
};

export default function MediaPlayer(props: Props) {
  const {
    mode,
    provider,
    cloudPublicId,
    posterPublicId,
    className,
    autoOnVisible = true,
    externalVideoRef,
    onPlay,
    onError,
  } = props;

  const { posterUrl, kind } = resolveMediaV2(
    { mode, provider, cloudPublicId } as ResolveInput,
    { posterId: posterPublicId }
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const opTokenRef = useRef<number>(0); // race condition guard
  const [armed, setArmed] = useState<boolean>(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Ensure only one video plays at a time
  useActiveVideoManager(videoRef.current);

  // Visibility hook: only relevant to full mode
  const { ref: hostRef, inView } = useInViewport<HTMLDivElement>({
    threshold: 0.5,
    rootMargin: "150px",
  });

  const isTapToPlay = mode !== "full";

  const arm = async () => {
    const el = videoRef.current;
    if (!el || armed || !cloudPublicId) return;
    mlog("arm()", { mode, cloudPublicId });

    try {
      // Mirror ref outwards for analytics hooks
      if (externalVideoRef && 'current' in externalVideoRef) {
        (externalVideoRef as any).current = el;
      }

      // iOS requirements before setting source
      el.muted = true;
      el.playsInline = true;
      el.controls = false;
      el.style.background = 'transparent';

      const url = await resolvePlayableMp4V2({ provider, cloudPublicId, mode } as ResolveInput);
      
      // Clear any existing sources before appending
      while (el.firstChild) el.removeChild(el.firstChild);
      const s = document.createElement('source');
      s.src = url;
      s.type = 'video/mp4';
      el.appendChild(s);
      el.load();
      
      setArmed(true);
      mlog("armed successfully", { url });
    } catch (err) {
      mwarn("arm() failed", err);
      setHasError(true);
      onError?.();
    }
  };

  const cleanup = () => {
    const el = videoRef.current;
    if (!el) return;
    mlog("cleanup()");
    try { el.pause(); } catch {}
    try { el.removeAttribute("src"); } catch {}
    // Remove child <source> nodes
    try {
      while (el.firstChild) el.removeChild(el.firstChild);
      el.load();
    } catch {}
    setPlaying(false);
    setReady(false);
    setArmed(false);
  };

  const tryPlay = async () => {
    const el = videoRef.current;
    if (!el) return false;
    
    const myToken = ++opTokenRef.current; // claim operation token
    el.muted = true;
    el.playsInline = true;

    try {
      await el.play();
      // Check if we were interrupted by pause
      if (myToken !== opTokenRef.current) {
        mlog("play() cancelled by newer operation");
        return false;
      }
      setPlaying(true);
      mlog("play() success");
      onPlay?.();
      return true;
    } catch (err) {
      mwarn("play() blocked/failed", err);
      setPlaying(false);
      return false;
    }
  };

  const safePause = () => {
    const el = videoRef.current;
    if (!el) return;
    
    opTokenRef.current++; // invalidate any pending play
    try { 
      el.pause();
      setPlaying(false);
      mlog("pause() called");
    } catch (err) {
      mwarn("pause() failed", err);
    }
  };

  // tap-to-play handler
  const onTap = async () => {
    if (!cloudPublicId) return;
    await arm();
    await tryPlay();
  };

  // Full Mode: visibility-driven autoplay/pause
  useEffect(() => {
    if (mode !== 'full' || !autoOnVisible || kind !== 'video') return;

    const el = videoRef.current;
    if (!el || !cloudPublicId) return;

    if (inView && !document.hidden) {
      // entering viewport: arm then play
      (async () => {
        await arm();
        await tryPlay();
      })();
    } else {
      // leaving viewport: use safe pause
      safePause();
    }

    // pause when tab hidden
    const vh = () => {
      if (document.hidden) {
        safePause();
      } else if (inView) {
        (async () => {
          await arm();
          await tryPlay();
        })();
      }
    };
    document.addEventListener("visibilitychange", vh);
    return () => document.removeEventListener("visibilitychange", vh);
  }, [mode, autoOnVisible, inView, cloudPublicId, kind]);

  // ready/playing events to fade poster→video without blank
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !armed) return;

    // Robust ready check: wait for actual playback
    const readyVideo = (v: HTMLVideoElement) =>
      v.readyState >= 3 || (v.currentTime ?? 0) > 0;

    const reveal = () => {
      if (readyVideo(el)) {
        el.controls = isTapToPlay && playing;
        setReady(true);
        mlog("reveal @", performance.now().toFixed(0), "ms");
      }
    };

    const onError = () => {
      setHasError(true);
      mwarn("video error event");
      onError?.();
    };

    el.addEventListener('playing', reveal, { once: true });
    el.addEventListener('timeupdate', reveal);
    el.addEventListener('loadeddata', reveal);
    el.addEventListener('error', onError);

    const safetyTimeout = setTimeout(reveal, 1500);

    return () => {
      el.removeEventListener('playing', reveal);
      el.removeEventListener('timeupdate', reveal);
      el.removeEventListener('loadeddata', reveal);
      el.removeEventListener('error', onError);
      clearTimeout(safetyTimeout);
    };
  }, [armed, playing, isTapToPlay]);

  // cleanup on unmount
  useEffect(() => cleanup, []);

  if (kind !== 'video') {
    return (
      <img
        src={posterUrl}
        alt=""
        className={`w-full aspect-[16/9] object-cover ${className ?? ''}`}
        loading="lazy"
        decoding="async"
        onError={(e) => { (e.currentTarget as any).src = '/img/poster-fallback.svg'; }}
      />
    );
  }

  const videoCls = `absolute inset-0 w-full h-full object-cover transition-opacity duration-[180ms] ease-out ${ready ? 'opacity-100' : 'opacity-0'}`;
  const posterCls = `absolute inset-0 w-full h-full object-cover select-none transition-opacity duration-[180ms] ease-out ${ready ? 'opacity-0' : 'opacity-100'}`;

  return (
    <div ref={hostRef} className={`relative aspect-video ${className ?? ''}`}>
      {/* Poster: cross-fades out when video is ready */}
      <img
        src={posterUrl}
        alt="Video poster"
        className={posterCls}
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={(e) => { (e.currentTarget as any).src = '/img/poster-fallback.svg'; }}
      />

      {/* Video: sits on top, cross-fades in when ready */}
      <video
        ref={videoRef}
        poster={posterUrl}
        preload="none"
        playsInline
        muted
        className={videoCls}
      />

      {/* Tap overlay for lite mode */}
      {isTapToPlay && !armed && (
        <button
          type="button"
          className="absolute inset-0 grid place-items-center z-10"
          aria-label="Tap to play"
          onClick={onTap}
        >
          <div className="px-4 py-2 rounded-full bg-black/60 text-white backdrop-blur-sm">
            Tap to play
          </div>
        </button>
      )}
    </div>
  );
}
